/**
 * ★ Fase 43 — FunnelLanding: landing pubblica parametrica per i funnel verticali.
 * URL: /f/:code  (it-al, it-lo, it-ba, es-id, es-th, en-id, en-al)
 *
 * - Lingua FORZATA a quella del funnel (nessun LanguageSwitcher).
 * - Prodotti caricati da GET /api/public/funnel/{code} (solo il mercato giusto).
 * - Lead inviati a POST /api/public/funnel-lead → CRM del tenant + tag acronimo.
 *
 * Struttura derivata dal funnel IT (hero → video → prodotti → trust → form).
 * I contenuti hero/copy sono provvisori: verranno sostituiti col materiale
 * definitivo del cliente, funnel per funnel.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  MapPin, ShieldCheck, FileText, Users, ArrowRight, Send, Loader2,
  CheckCircle2, Building2, PlayCircle,
} from 'lucide-react';
import { BRAND, LOCATIONS } from '../../lib/brand';
import { getFunnelClient } from '../../lib/funnels';
import { setPageMeta, ogImageFor } from '../../lib/seo';

import { TestimonialsSection } from '../../components/funnels/TestimonialsSection';
export default function FunnelLanding() {
  const { code } = useParams();
  const { t } = useTranslation();
  const funnel = useMemo(() => getFunnelClient(code), [code]);
  const [data, setData] = useState(null);        // {code, properties, ...}
  const [loadError, setLoadError] = useState(false);
  const [selectedProp, setSelectedProp] = useState(null);
  const formRef = useRef(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // ── Lingua FORZATA del funnel (niente selettore) ──────────────────────
  useEffect(() => {
    if (funnel?.language && i18n.language !== funnel.language) {
      i18n.changeLanguage(funnel.language);
    }
    // ★ Fase 89 — meta social del funnel parametrico (thumbnail per asset/mercato)
    if (funnel) {
      setPageMeta({
        title: `${funnel.heroTitle} — HCF Property`,
        description: funnel.heroSubtitle,
        image: ogImageFor(funnel.slug),
        imageAlt: `${funnel.heroTitle} — HCF Property`,
        path: `/f/${funnel.slug}`,
        locale: funnel.language,
      });
    }
  }, [funnel]);

  useEffect(() => {
    if (!funnel) return;
    setData(null);
    setLoadError(false);
    api.get(`/public/funnel/${funnel.slug}`)
      .then((r) => setData(r.data))
      .catch(() => setLoadError(true));
  }, [funnel]);

  const scrollToForm = (prop = null) => {
    setSelectedProp(prop);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSend = name.trim().length >= 2 && emailOk && !sending;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSend) return;
    setSending(true);
    try {
      await api.post('/public/funnel-lead', {
        funnel: funnel.slug,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        property_slug: selectedProp?.slug || null,
        message: message.trim() || null,
      });
      setSent(true);
      toast.success(t('funnel.formOkTitle', 'Richiesta inviata!'), {
        description: t('funnel.formOkDesc', 'Un consulente HCF Property ti contatterà a breve.'),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('funnel.formError', 'Invio non riuscito, riprova.'));
    } finally {
      setSending(false);
    }
  };

  if (!funnel) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--gp-parchment)] p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <img src={BRAND.logo} alt="HCF Property" className="h-12 mx-auto" />
            <div className="font-serif text-xl text-[var(--gp-emerald)]" data-testid="funnel-not-found">
              {t('funnel.notFound', 'Pagina non trovata')}
            </div>
            <p className="text-sm text-muted-foreground">
              {t('funnel.notFoundDesc', 'Il link che hai seguito non corrisponde a nessuna delle nostre destinazioni.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const props = data?.properties || [];

  return (
    <div className="min-h-screen bg-[var(--gp-parchment)] text-[var(--gp-ink,#0B1211)]" data-testid={`funnel-page-${funnel.slug}`}>
      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-[var(--gp-ivory)]/90 backdrop-blur-md border-b border-[var(--gp-stone,#D7D0C4)]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={BRAND.logo} alt="HCF Property" className="h-9 w-auto shrink-0" />
            <Badge
              variant="outline"
              className="border-[var(--gp-gold)]/60 text-[var(--gp-emerald)] bg-[var(--gp-gold)]/10 text-[11px] uppercase tracking-wider hidden sm:inline-flex"
              data-testid="funnel-market-badge"
            >
              <MapPin className="w-3 h-3 mr-1" /> {funnel.marketLabel}
            </Badge>
          </div>
          <Button
            onClick={() => scrollToForm(null)}
            className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)] h-10"
            data-testid="funnel-header-cta"
          >
            {t('funnel.headerCta', 'Richiedi informazioni')}
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={funnel.heroImage} alt={funnel.marketLabel} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(11,18,17,0.88)] via-[rgba(11,18,17,0.55)] to-[rgba(11,18,17,0.25)]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-24 md:py-36">
          <Badge className="bg-[var(--gp-gold)]/90 text-[var(--gp-forest)] border-0 uppercase tracking-widest text-[10px] mb-4" data-testid="funnel-hero-kicker">
            HCF Property · {funnel.marketLabel}
          </Badge>
          <h1 className="font-serif text-4xl md:text-6xl text-[var(--gp-ivory)] max-w-3xl leading-tight" data-testid="funnel-hero-title">
            {funnel.heroTitle}
          </h1>
          <p className="mt-5 text-base md:text-lg text-[var(--gp-sand,#E9E1D3)] max-w-2xl" data-testid="funnel-hero-subtitle">
            {funnel.heroSubtitle}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              onClick={() => scrollToForm(null)}
              className="bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
              data-testid="funnel-hero-cta"
            >
              {t('funnel.heroCta', 'Parla con un consulente')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            {funnel.videoId && (
              <a href="#video" data-testid="funnel-hero-video-link">
                <Button size="lg" variant="outline" className="border-[var(--gp-ivory)]/60 bg-transparent text-[var(--gp-ivory)] hover:bg-[var(--gp-ivory)]/10 hover:text-[var(--gp-ivory)]">
                  <PlayCircle className="w-4 h-4 mr-2" /> {t('funnel.watchVideo', 'Guarda il video')}
                </Button>
              </a>
            )}
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-[13px] text-[var(--gp-sand,#E9E1D3)]/90">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[var(--gp-gold-soft)]" /> {t('funnel.trustVerification', 'Verification Pass on-site')}</span>
            <span className="inline-flex items-center gap-1.5"><FileText className="w-4 h-4 text-[var(--gp-gold-soft)]" /> {t('funnel.trustDocs', 'Documentazione completa')}</span>
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-[var(--gp-gold-soft)]" /> {t('funnel.trustCommunity', 'Community internazionale HCF')}</span>
          </div>
        </div>
      </section>

      {/* ── Video (se disponibile per il mercato) ── */}
      {funnel.videoId && (
        <section id="video" className="max-w-5xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <h2 className="font-serif text-2xl md:text-4xl text-[var(--gp-emerald)] text-center" data-testid="funnel-video-title">
            {t('funnel.videoTitle', 'Guarda il progetto con i tuoi occhi')}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground text-center mt-2 max-w-xl mx-auto">
            {t('funnel.videoSubtitle', 'Verification Pass: il nostro team documenta on-site ogni progetto prima di proportelo.')}
          </p>
          <div className="mt-8 aspect-video rounded-xl overflow-hidden shadow-xl border border-[var(--gp-stone,#D7D0C4)]" data-testid="funnel-video-embed">
            <iframe
              src={`https://www.youtube.com/embed/${funnel.videoId}?rel=0&modestbranding=1`}
              title={`HCF Property · ${funnel.marketLabel}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>
      )}

      {/* ── ★ 2026-10-19 — Testimonianze video (fonte unica lib/testimonials.js) ── */}
      <TestimonialsSection lang={funnel.language || 'it'} theme="light" testIdPrefix="funnel" id="testimonianze" />

      {/* ── Schede prodotto del mercato ── */}
      <section className="bg-[var(--gp-ivory)] border-y border-[var(--gp-stone,#D7D0C4)]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <h2 className="font-serif text-2xl md:text-4xl text-[var(--gp-emerald)]" data-testid="funnel-products-title">
            {t('funnel.productsTitle', 'Le opportunità disponibili')}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-2xl">
            {t('funnel.productsSubtitle', { defaultValue: 'Solo progetti {{market}}: selezionati, verificati e seguiti dal nostro team locale.', market: funnel.marketLabel })}
          </p>

          {loadError ? (
            <Card className="mt-8"><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="funnel-products-error">
              {t('funnel.productsError', 'Impossibile caricare i progetti. Riprova tra poco.')}
            </CardContent></Card>
          ) : data === null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
            </div>
          ) : props.length === 0 ? (
            <Card className="mt-8"><CardContent className="p-10 text-center" data-testid="funnel-products-empty">
              <Building2 className="w-10 h-10 mx-auto text-[var(--gp-gold)] mb-3" />
              <div className="font-serif text-lg text-[var(--gp-emerald)]">{t('funnel.comingSoonTitle', 'Progetti in arrivo')}</div>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                {t('funnel.comingSoonDesc', 'Stiamo finalizzando le prossime opportunità per questo mercato. Lascia i tuoi contatti per riceverle in anteprima.')}
              </p>
              <Button className="mt-5 bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]" onClick={() => scrollToForm(null)} data-testid="funnel-empty-cta">
                {t('funnel.comingSoonCta', 'Avvisami in anteprima')}
              </Button>
            </CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8" data-testid="funnel-products-grid">
              {props.map((p) => (
                <Card key={p.slug} className="overflow-hidden group hover:shadow-xl transition-shadow duration-200" data-testid={`funnel-product-${p.slug}`}>
                  <div className="h-48 overflow-hidden relative">
                    <img
                      src={p.image_url || p.hero_image}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    {(p.apartments?.available ?? null) !== null && (
                      <Badge className="absolute top-3 left-3 bg-[var(--gp-ivory)]/95 text-[var(--gp-emerald)] border-0 text-[11px]">
                        {t('funnel.unitsAvailable', { defaultValue: '{{count}} unità disponibili', count: p.apartments.available })}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-5">
                    <div className="font-serif text-lg text-[var(--gp-emerald)]">{p.name}</div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {p.location}
                    </div>
                    {p.tagline && <p className="text-sm mt-2 line-clamp-2">{p.tagline}</p>}
                    <Button
                      className="w-full mt-4 bg-[var(--gp-emerald)] text-[var(--gp-ivory)] hover:bg-[var(--gp-emerald-2,#0F4A37)]"
                      onClick={() => scrollToForm(p)}
                      data-testid={`funnel-product-cta-${p.slug}`}
                    >
                      {t('funnel.productCta', 'Richiedi la scheda completa')}
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Perché HCF ── */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <h2 className="font-serif text-2xl md:text-4xl text-[var(--gp-emerald)] text-center">
          {t('funnel.whyTitle', 'Perché investire con HCF Property')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {[
            { icon: ShieldCheck, title: t('funnel.why1Title', 'Verifica on-site'), desc: t('funnel.why1Desc', 'Ogni progetto è visitato e documentato dal nostro team prima di essere proposto.') },
            { icon: FileText, title: t('funnel.why2Title', 'Trasparenza legale'), desc: t('funnel.why2Desc', 'Contratti, permessi e documentazione completa disponibili prima di ogni impegno.') },
            { icon: Building2, title: t('funnel.why3Title', 'Gestione inclusa'), desc: t('funnel.why3Desc', 'Progetti seguiti dalla costruzione alla messa a reddito, senza pensieri.') },
            { icon: Users, title: t('funnel.why4Title', 'Community reale'), desc: t('funnel.why4Desc', 'Centinaia di investitori ci hanno già scelto nei nostri mercati internazionali.') },
          ].map((item, i) => (
            <Card key={i} className="bg-[var(--gp-ivory)]">
              <CardContent className="p-6">
                <item.icon className="w-7 h-7 text-[var(--gp-gold)]" />
                <div className="font-medium mt-3">{item.title}</div>
                <p className="text-sm text-muted-foreground mt-1.5">{item.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* ── Form contatti ── */}
      <section ref={formRef} id="contatti" className="bg-[var(--gp-emerald)]">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <h2 className="font-serif text-2xl md:text-4xl text-[var(--gp-ivory)] text-center" data-testid="funnel-form-title">
            {t('funnel.formTitle', 'Richiedi informazioni senza impegno')}
          </h2>
          <p className="text-sm md:text-base text-[var(--gp-sand,#E9E1D3)]/90 text-center mt-2">
            {t('funnel.formSubtitle', 'Un consulente dedicato ti ricontatta entro 24 ore lavorative.')}
          </p>

          <Card className="mt-8 bg-[var(--gp-ivory)]">
            <CardContent className="p-6 md:p-8">
              {sent ? (
                <div className="text-center py-8" data-testid="funnel-form-success">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-[var(--gp-success,#1F7A5A)]" />
                  <div className="font-serif text-xl text-[var(--gp-emerald)] mt-3">
                    {t('funnel.formOkTitle', 'Richiesta inviata!')}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('funnel.formOkDesc', 'Un consulente HCF Property ti contatterà a breve.')}
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  {selectedProp && (
                    <div className="rounded-md border border-[var(--gp-gold)]/50 bg-[var(--gp-gold)]/10 px-3 py-2 text-sm text-[var(--gp-emerald)]" data-testid="funnel-form-property">
                      {t('funnel.formPropertyInterest', 'Interesse per:')} <b>{selectedProp.name}</b>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wide">{t('funnel.formName', 'Nome e cognome')} *</Label>
                      <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="funnel-form-name" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide">Email *</Label>
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="funnel-form-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide">{t('funnel.formPhone', 'Telefono (consigliato)')}</Label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" data-testid="funnel-form-phone" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide">{t('funnel.formMessage', 'Messaggio (opzionale)')}</Label>
                    <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="mt-1" data-testid="funnel-form-message" />
                  </div>
                  <Button
                    type="submit"
                    disabled={!canSend}
                    className="w-full h-11 bg-[var(--gp-gold)] text-[var(--gp-forest)] hover:bg-[var(--gp-gold-soft)]"
                    data-testid="funnel-form-submit"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    {t('funnel.formSubmit', 'Invia richiesta')}
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center">
                    {t('funnel.formPrivacy', 'Inviando accetti la nostra')}{' '}
                    <Link to="/privacy" className="underline hover:text-[var(--gp-emerald)]" data-testid="funnel-privacy-link">Privacy Policy</Link>.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-[var(--gp-ink,#0B1211)] text-[var(--gp-sand,#E9E1D3)]/80">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <img src={BRAND.logo} alt="HCF Property" className="h-9" />
              <p className="text-xs mt-2 max-w-sm">{BRAND.tagline}</p>
            </div>
            <div className="text-xs space-y-1">
              <div>{LOCATIONS.hq_bulgaria.label} — {LOCATIONS.hq_bulgaria.city}</div>
              <div className="flex gap-4 pt-1">
                <Link to="/privacy" className="underline hover:text-[var(--gp-gold-soft)]" data-testid="funnel-footer-privacy">Privacy</Link>
                <Link to="/terms" className="underline hover:text-[var(--gp-gold-soft)]" data-testid="funnel-footer-terms">{t('funnel.footerTerms', 'Termini')}</Link>
                <Link to="/cookie-policy" className="underline hover:text-[var(--gp-gold-soft)]" data-testid="funnel-footer-cookies">Cookie</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-5 text-[11px] flex flex-wrap justify-between gap-2">
            <span>© {new Date().getFullYear()} HCF Property</span>
            <span className="uppercase tracking-wider" data-testid="funnel-footer-code">{funnel.code}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
